import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { ReportType } from "src/interfaces/enums";

const getReportArtifact = (reportId) => {
  return axios
    .get(`${nuclearn_api_url}/reports/run-report`, {
      params: { report_id: reportId },
    })
    .then((response) => {
      return response.data;
    });
};

const useGetReportArtifact = (reportId) => {
  return useQuery(["report", reportId], () => getReportArtifact(reportId), {
    refetchOnWindowFocus: false,
    enabled: !!reportId,
  });
};

const getReport = (reportId) => {
  return axios
    .get(`${nuclearn_api_url}/reports/report`, {
      params: { report_id: reportId },
    })
    .then((response) => {
      return response.data;
    });
};

const useGetReport = (reportId) => {
  return useQuery(["report", reportId], () => getReport(reportId), {
    refetchOnWindowFocus: false,
    enabled: !!reportId,
  });
};

const getReportWithBuckets = (reportId) => {
  return axios
    .get(`${nuclearn_api_url}/reports/report-with-bucket-details`, {
      params: { report_id: reportId },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetReportWithBuckets = (reportId) => {
  return useQuery(
    ["reportWithBuckets", reportId],
    () => getReportWithBuckets(reportId),
    {
      refetchOnWindowFocus: false,
      enabled: !!reportId,
    },
  );
};

const getReportList = (reportType) => {
  return axios
    .get(`${nuclearn_api_url}/reports/?report_type=${reportType}`)
    .then((response) => {
      return response.data.sort((a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });
    });
};

export const useReportList = (queryParams = {}) => {
  return useQuery(
    [queryParams === ReportType.ANALYTIC ? "reportList" : "reportTemplateList"],
    () => getReportList(queryParams),
    {
      refetchOnWindowFocus: false,
    },
  );
};

export const uploadReportTemplate = (reportData) => {
  return axios({
    method: "put",
    url: `${nuclearn_api_url}/reports/upload-template`,
    data: reportData.reportTemplate.templateFile,
    params: {
      report_type: reportData.reportType,
      report_template_name: reportData.reportTemplate.templateName,
      report_template_description:
        reportData.reportTemplate.templateDescription,
      overwrite_existing_file: reportData.reportTemplate.overwriteExistingFile,
    },
  }).then((response) => {
    return response.data;
  });
};

export const useUploadReportTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(uploadReportTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportTemplateList");
    },
    onError: (error) => {
      console.error(error.response);
    },
  });
  return mutation;
};

export const generateTemplateDoc = (reportTemplateId, datasetId, sourceUid) => {
  if (!reportTemplateId || !datasetId || !sourceUid) {
    return Promise.resolve(null);
  }
  return axios
    .get(
      `${nuclearn_api_url}/reports/run-generative-report?report_template_id=${reportTemplateId}&dataset_id=${datasetId}&source_uid=${sourceUid}`,
      {
        // will be a docx file
        responseType: "blob",
      },
    )
    .then((response) => {
      return response.data;
    });
};

export const useGenerateTemplateDoc = ({
  reportTemplateId,
  datasetId,
  sourceUid,
}) => {
  return useQuery(
    ["generateReportTemplateDoc"],
    () => generateTemplateDoc(reportTemplateId, datasetId, sourceUid),
    {
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * @param {string | null | undefined} queryParams
 */
const getReportTemplateList = async (queryParams) => {
  const url = typeof queryParams === "string" && queryParams
    ? `${nuclearn_api_url}/reports/report-templates?report_type=${queryParams}`
    : `${nuclearn_api_url}/reports/report-templates`;
  const response = await axios.get(url);
  return response.data.sort((a, b) => {
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });
};

/**
 * @param {string | null | undefined} queryParams
 */
export const useReportTemplateList = (queryParams = null) => {
  return useQuery(
    ["reportTemplateList", queryParams],
    () => getReportTemplateList(queryParams),
    {
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * @param {number} reportTemplateId
 */
const getReportTemplateById = async (reportTemplateId) => {
  const response = await axios.get(
    `${nuclearn_api_url}/reports/report-templates/${reportTemplateId}`,
  );
  return response.data;
};

/**
 * @param {number | null | undefined} reportTemplateId
 */
export const useReportTemplateById = (reportTemplateId) => {
  return useQuery(
    ["reportTemplate", "detail", reportTemplateId],
    () => getReportTemplateById(reportTemplateId),
    {
      refetchOnWindowFocus: false,
      enabled: !!reportTemplateId,
    },
  );
};

const createReport = (reportInfo) => {
  return axios
    .put(nuclearn_api_url + "/reports/", reportInfo)
    .then((response) => {
      return response.data;
    });
};

export const useCreateReport = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createReport, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

//TODO: needs to be able to add buckets to the reports
const updateReport = ({ reportId, report }) => {
  return axios
    .patch(`${nuclearn_api_url}/reports/${reportId}`, report)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateReport = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateReport, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};
const updateReportTemplate = ({ reportTemplateId, reportTemplate }) => {
  return axios
    .patch(
      `${nuclearn_api_url}/reports/template/${reportTemplateId}`,
      reportTemplate,
    )
    .then((response) => {
      return response.data;
    });
};
const deleteReportTemplate = (reportTemplateId) => {
  return axios
    .delete(`${nuclearn_api_url}/reports/template/${reportTemplateId}`)
    .then(() => {
      return reportTemplateId;
    });
};

export const useDeleteReportTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteReportTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportTemplateList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const useUpdateReportTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateReportTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportTemplateList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteReport = (reportId) => {
  return axios
    .delete(`${nuclearn_api_url}/reports/`, {
      params: { report_id: reportId },
    })
    .then(() => {
      return reportId;
    });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteReport, {
    onSuccess: () => {
      queryClient.invalidateQueries("reportList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getReportTemplateArtifacts = (reportTemplateId) => {
  return axios
    .get(`${nuclearn_api_url}/reports/report-template-artifacts`, {
      params: { report_template_id: reportTemplateId },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetReportTemplateArtifacts = (reportTemplateId) => {
  return useQuery(
    ["reportTemplate", reportTemplateId],
    () => getReportTemplateArtifacts(reportTemplateId),
    {
      refetchOnWindowFocus: false,
      enabled: !!reportTemplateId,
    },
  );
};
